import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";import Header from "@/components/Header";
import api, { API, getAuthToken, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import TierBadge from "@/components/TierBadge";
import { CATEGORIES } from "@/pages/ClientCategoriesPage";
import { TIERS, TIER_ORDER, eligibleTier, nextTier } from "@/lib/tiers";
import { Plus, MapPin, Briefcase, HardHat, TrendUp, Clock, Gift, Copy, ShieldCheck, UploadSimple, X } from "@phosphor-icons/react";

const STATUS_TAG = {
  open: "Open",
  awaiting_admin_approval: "Awaiting admin",
  awaiting_payment: "Awaiting payment",
  in_progress: "In progress",
  awaiting_confirmation: "Awaiting confirmation",
  awaiting_release: "Awaiting release",
  completed: "Completed",
};

const JobCard = ({ job, showClient }) => {
  const cat = CATEGORIES.find((c) => c.id === job.category);
  const token = getAuthToken();
  const firstPhoto = job.photo_ids?.[0];
  const photoUrl = firstPhoto ? `${API}/uploads/${firstPhoto}?auth=${token}` : null;

  return (
    <Link
      to={`/jobs/${job.id}`}
      className="flex overflow-hidden rounded-md border border-[#E5E2DB] bg-white transition-all hover:border-[#0A0F0D] hover:shadow-brutal"
      data-testid={`job-card-${job.id}`}
    >
      {photoUrl ? (
        <img src={photoUrl} alt="" className="h-32 w-32 flex-shrink-0 object-cover" />
      ) : (
        <div className="flex h-32 w-32 flex-shrink-0 items-center justify-center bg-[#FFFFFF]">
          <Briefcase size={28} weight="duotone" color="#5C605E" />
        </div>
      )}
      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-sm bg-[#0A0F0D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#FFFFFF]">
            {cat?.label || job.category}
          </span>
          <span className="rounded-sm border border-[#E5E2DB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#5C605E]">
            <Clock size={10} weight="bold" className="mr-1 inline align-[-1px]" />
            {STATUS_TAG[job.status] || job.status}
          </span>
          {job.budget && (
            <span className="text-xs text-[#5C605E]">Budget: {job.budget}</span>
          )}
        </div>
        <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-[#0A0F0D]">
          {job.title}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-[#5C605E]">{job.description}</p>
        <div className="mt-auto flex items-center justify-between pt-3 text-xs text-[#5C605E]">
          <span className="inline-flex items-center gap-1">
            <MapPin size={12} weight="bold" />
            {job.location?.address || "Pinned on map"}
          </span>
          {showClient && <span>Posted by {job.client_name || "Client"}</span>}
        </div>
      </div>
    </Link>
  );
};

const ReferralCard = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/users/referral");
        setData(r.data);
      } catch (e) {}
    })();
  }, []);
  if (!data) return null;

  const shareUrl = `${window.location.origin}/auth?ref=${data.referral_code}`;
  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      className="rounded-md border border-[#E5E2DB] bg-white p-6 md:p-8"
      data-testid="referral-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#5C605E]">
            <Gift size={12} weight="fill" className="mr-1 inline align-[-1px] text-[#C65D47]" />
            Referral rewards
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-[#0A0F0D]">
            R{Number(data.reward_amount).toFixed(0)} per friend
          </h2>
          <p className="mt-1 text-sm text-[#5C605E]">
            You have <strong className="text-[#0A0F0D]" data-testid="referral-credit-balance">R{Number(data.credit_balance).toFixed(2)}</strong> in
            credit, auto-applied on your next payment.
          </p>
        </div>
        <div className="rounded-md border border-[#0A0F0D] px-3 py-2 text-right">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-[#5C605E]">
            Referred
          </p>
          <p className="font-display text-xl font-bold text-[#0A0F0D]" data-testid="referral-count">
            {data.rewarded_count}/{data.referred_count}
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-2 sm:grid-cols-[auto_1fr_auto]">
        <span className="rounded-sm bg-[#0A0F0D] px-3 py-2 font-mono text-sm font-bold text-white" data-testid="referral-code">
          {data.referral_code}
        </span>
        <Input
          readOnly
          value={shareUrl}
          onFocus={(e) => e.target.select()}
          data-testid="referral-share-url"
          className="border-[#E5E2DB] bg-white font-mono text-xs"
        />
        <Button
          onClick={() => copy(shareUrl, "Link")}
          data-testid="referral-copy-button"
          className="bg-[#C65D47] text-white hover:bg-[#0A0F0D]"
        >
          <Copy size={16} weight="bold" className="mr-2" />
          Copy link
        </Button>
      </div>
    </div>
  );
};

const IdVerificationCard = ({ profile, onRefresh }) => {
  const status = profile?.id_verification_status || "none";
  const fileInputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: photo } = await api.post("/uploads/photo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/contractors/id-verification/submit", { id_photo_id: photo.id });
      toast.success("ID uploaded — awaiting admin verification");
      await onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setUploading(false);
    }
  };

  const statusColor = {
    verified: { color: "#FFFFFF", bg: "#0A0F0D" },
    pending: { color: "#8B5E34", bg: "#FDECD0" },
    rejected: { color: "#FFFFFF", bg: "#C65D47" },
    none: { color: "#5C605E", bg: "#E5E2DB" },
  }[status];

  return (
    <div
      className="rounded-md border border-[#E5E2DB] bg-white p-6 md:p-8"
      data-testid="id-verification-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#5C605E]">
            <ShieldCheck size={12} weight="fill" className="mr-1 inline align-[-1px] text-[#C65D47]" />
            Identity verification
          </p>
          <h2 className="mt-2 font-display text-2xl font-bold text-[#0A0F0D]">
            Get the Verified ID badge
          </h2>
          <p className="mt-1 max-w-md text-sm text-[#5C605E]">
            Upload a photo of your government-issued ID. Once admin approves, a Verified ID badge appears next to your name across ConnectAPro — clients trust verified pros far more.
          </p>
        </div>
        <span
          className="rounded-sm px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest"
          style={{ backgroundColor: statusColor.bg, color: statusColor.color }}
          data-testid={`id-verification-status-${status}`}
        >
          {status === "none" ? "Not submitted" : status}
        </span>
      </div>

      {status === "none" || status === "rejected" ? (
        <div className="mt-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid="id-file-input"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="id-upload-button"
            className="bg-[#0A0F0D] text-white hover:bg-[#C65D47]"
          >
            <UploadSimple size={16} weight="bold" className="mr-2" />
            {uploading ? "Uploading…" : status === "rejected" ? "Try again" : "Upload ID"}
          </Button>
          {status === "rejected" && (
            <p className="mt-2 text-xs text-[#C65D47]">
              Your last submission was rejected. Please upload a clearer photo.
            </p>
          )}
        </div>
      ) : status === "pending" ? (
        <p className="mt-4 text-sm text-[#5C605E]">
          Your ID is with our verification team. This usually takes 1–2 business days.
        </p>
      ) : (
        <p className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#0A0F0D]">
          <ShieldCheck size={14} weight="fill" /> Verified — your badge is live on Find a Pro.
        </p>
      )}
    </div>
  );
};

const ClientDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/jobs?mine=true");
        setJobs(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="client-dashboard-page">
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12 md:px-10 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
              Your workspace
            </p>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl">
              Your posted jobs
            </h1>
          </div>
          <Link to="/client/categories">
            <Button
              data-testid="post-new-job-button"
              className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
            >
              <Plus size={16} weight="bold" className="mr-2" />
              Post another job
            </Button>
          </Link>
        </div>

        <div className="mt-10">
          <ReferralCard />
        </div>

        <div className="mt-10 space-y-4" data-testid="client-jobs-list">
          {loading ? (
            <p className="text-[#5C605E]">Loading…</p>
          ) : jobs.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#D6D3CD] bg-white p-12 text-center">
              <Briefcase size={40} weight="duotone" color="#5C605E" className="mx-auto" />
              <p className="mt-4 font-display text-xl font-semibold text-[#0A0F0D]">
                No jobs yet
              </p>
              <p className="mt-1 text-sm text-[#5C605E]">
                Post your first job to get quotes from local pros.
              </p>
              <Link to="/client/categories">
                <Button
                  data-testid="empty-post-job-button"
                  className="mt-5 bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
                >
                  Post a job
                </Button>
              </Link>
            </div>
          ) : (
            jobs.map((j) => <JobCard key={j.id} job={j} />)
          )}
        </div>
      </main>
    </div>
  );
};

const TierCard = ({ profile, onRequest }) => {
  const currentTier = profile?.tier || "none";
  const completed = profile?.completed_jobs || 0;
  const pending = profile?.pending_tier_request;
  const eligible = eligibleTier(completed, currentTier);
  const next = nextTier(currentTier);
  const nextCfg = next ? TIERS[next] : null;
  const currentIdx = TIER_ORDER.indexOf(currentTier);
  const progressPct = nextCfg
    ? Math.min(100, Math.round((completed / nextCfg.threshold) * 100))
    : 100;

  return (
    <div
      className="rounded-md border border-[#E5E2DB] bg-white p-6 md:p-8"
      data-testid="tier-progress-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#5C605E]">
            Reliability tier
          </p>
          <div className="mt-2 flex items-center gap-3">
            <TierBadge tier={currentTier} size="md" />
            <span className="font-display text-lg font-bold text-[#0A0F0D]">
              {TIERS[currentTier].label}
            </span>
          </div>
          <p className="mt-2 text-sm text-[#5C605E]">
            <strong className="text-[#0A0F0D]">{completed}</strong> job{completed === 1 ? "" : "s"} completed
          </p>
        </div>
        {pending ? (
          <div className="rounded-md border border-[#8B5E34] bg-[#FDECD0] px-3 py-2 text-xs font-semibold text-[#8B5E34]">
            Pending admin approval for <TierBadge tier={pending} size="sm" showLabel />
          </div>
        ) : eligible ? (
          <Button
            onClick={() => onRequest(eligible)}
            data-testid="request-tier-button"
            className="bg-[#0A0F0D] text-[#FFFFFF] hover:bg-[#C65D47]"
          >
            <TrendUp size={16} weight="bold" className="mr-2" />
            Request {TIERS[eligible].label} upgrade
          </Button>
        ) : null}
      </div>

      {nextCfg && (
        <div className="mt-6">
          <div className="flex items-center justify-between text-xs text-[#5C605E]">
            <span>Progress to {nextCfg.label}</span>
            <span>
              {completed} / {nextCfg.threshold}
            </span>
          </div>
          <Progress value={progressPct} className="mt-2 h-2 bg-[#E5E2DB]" />
        </div>
      )}

      <div className="mt-6 grid grid-cols-5 gap-2">
        {TIER_ORDER.slice(1).map((t, i) => {
          const cfg = TIERS[t];
          const reached = i + 1 <= currentIdx;
          return (
            <div
              key={t}
              className={`rounded-md border p-2 text-center transition-all ${
                reached ? "border-[#0A0F0D]" : "border-dashed border-[#D6D3CD] opacity-60"
              }`}
              data-testid={`tier-step-${t}`}
            >
              <div className="mx-auto flex justify-center">
                <TierBadge tier={t} size="sm" showLabel={false} />
              </div>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-[#0A0F0D]">
                {cfg.label}
              </p>
              <p className="text-[10px] text-[#5C605E]">{cfg.threshold} jobs</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ContractorDashboard = () => {
  const { user, refreshUser } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const cat = user?.contractor_profile?.category;

  const load = useCallback(async () => {
    if (!cat) return;
    try {
      const { data } = await api.get(`/jobs?category=${cat}`);
      setJobs(data);
    } finally {
      setLoading(false);
    }
  }, [cat]);

  useEffect(() => {
    load();
  }, [load]);

  const requestTier = async (tier) => {
    try {
      await api.post("/contractors/tier-request", { requested_tier: tier });
      toast.success(`Requested ${TIERS[tier].label} upgrade — awaiting admin`);
      await refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="contractor-dashboard-page">
      <Toaster position="top-right" richColors />
      <Header />
      <main className="mx-auto max-w-5xl px-6 py-12 md:px-10 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#C65D47]">
              Contractor · {user?.contractor_profile?.business_name}
            </p>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-[#0A0F0D] sm:text-5xl">
              Open jobs in your category
            </h1>
            <p className="mt-2 text-sm text-[#5C605E]">
              Showing all <strong>{cat}</strong> jobs posted by clients.
            </p>
          </div>
          <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#C65D47]">
            <HardHat size={22} weight="duotone" color="#FFFFFF" />
          </div>
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <TierCard profile={user?.contractor_profile} onRequest={requestTier} />
          <IdVerificationCard profile={user?.contractor_profile} onRefresh={refreshUser} />
        </div>

        <div className="mt-10 space-y-4" data-testid="contractor-jobs-list">
          {loading ? (
            <p className="text-[#5C605E]">Loading…</p>
          ) : jobs.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#D6D3CD] bg-white p-12 text-center">
              <Briefcase size={40} weight="duotone" color="#5C605E" className="mx-auto" />
              <p className="mt-4 font-display text-xl font-semibold text-[#0A0F0D]">
                No jobs right now
              </p>
              <p className="mt-1 text-sm text-[#5C605E]">
                Check back soon — new jobs are posted daily in your area.
              </p>
            </div>
          ) : (
            jobs.map((j) => <JobCard key={j.id} job={j} showClient />)
          )}
        </div>
      </main>
    </div>
  );
};

const DashboardRouter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    if (user.role === "admin") {
      navigate("/admin");
      return;
    }
    if (!user.role) navigate("/role");
    else if (user.role === "contractor" && !user.contractor_profile)
      navigate("/contractor/register");
  }, [user, navigate]);

  if (!user) return null;
  if (user.role === "admin") return null;
  if (user.role === "contractor") return <ContractorDashboard />;
  return <ClientDashboard />;
};

export { ClientDashboard, ContractorDashboard };
export default DashboardRouter;
